import React, { useState, useEffect } from "react";
import { Button } from "../../../../ui/components/Button";
import { LinkButton } from "../../../../ui/components/LinkButton";
import { TextField } from "../../../../ui/components/TextField";
import { FeatherMail, FeatherLock } from "@subframe/core";

interface LoginFormProps {
  onSubmit: (email: string, password: string) => void;
  isLoading?: boolean;
  error?: string | null;
}

interface FormErrors {
  email?: string;
  password?: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  isLoading = false,
  error = null,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});

  // Parse error message and set field-specific errors
  useEffect(() => {
    if (error) {
      const errorLower = error.toLowerCase();
      if (
        errorLower.includes("email") ||
        errorLower.includes("no account found")
      ) {
        setErrors((prev) => ({
          ...prev,
          email: error,
          password: undefined,
        }));
      } else if (
        errorLower.includes("password") ||
        errorLower.includes("incorrect password")
      ) {
        setErrors((prev) => ({
          ...prev,
          password: error,
          email: undefined,
        }));
      } else {
        // General error - clear field-specific errors
        setErrors({
          email: undefined,
          password: undefined,
        });
      }
    } else {
      // Clear errors when error is cleared
      setErrors({
        email: undefined,
        password: undefined,
      });
    }
  }, [error]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Invalid email format";
    }

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit(email, password);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (errors.password) {
      setErrors((prev) => ({ ...prev, password: undefined }));
    }
  };

  return (
    <div className="flex w-full max-w-[448px] flex-col items-start justify-center gap-8">
      <div className="flex w-full flex-col items-start gap-1">
        <span className="w-full text-heading-2 font-heading-2 text-default-font">
          Sign in to your account
        </span>
        <span className="text-body font-body text-subtext-color">
          Enter your credentials to access your dashboard
        </span>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex w-full flex-col items-start justify-center gap-4"
      >
        <TextField
          className="h-auto w-full flex-none"
          label="Email"
          helpText={errors.email || ""}
          icon={<FeatherMail />}
        >
          <TextField.Input
            name="email"
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={handleEmailChange}
            disabled={isLoading}
          />
        </TextField>

        <TextField
          className="h-auto w-full flex-none"
          label="Password"
          helpText={errors.password || ""}
          icon={<FeatherLock />}
        >
          <TextField.Input
            name="password"
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={handlePasswordChange}
            disabled={isLoading}
          />
        </TextField>

        <div className="flex w-full items-end justify-end">
          <LinkButton
            size="small"
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              // Handle forgot password
            }}
          >
            Forgot password?
          </LinkButton>
        </div>

        <Button
          type="submit"
          className="h-8 w-full flex-none"
          disabled={isLoading}
        >
          {isLoading ? "Signing In..." : "Sign in"}
        </Button>
      </form>
    </div>
  );
};
